/**
 * The end of setup: the log is ready, and one offer before the first tap.
 * Starting a new log and joining a partner's both land here. The parent can
 * connect the AI assistant they already use (/assistant) or start logging;
 * skipping is one tap, and the log's one-time tip asks again after the first
 * entry. Not a 3am screen: it appears once, right after setup.
 */
import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { track } from "../lib/analytics.ts";
import { namesInWords, tryPhrases } from "../lib/assistants.ts";
import { useBabies, useBottleUnit } from "../lib/queries.ts";

export function Welcome() {
  const babies = useBabies();
  const unit = useBottleUnit();
  const names = (babies.data ?? []).map((b) => b.name);
  const answer = (accepted: boolean) => track("welcome answered", { accepted });

  return (
    <div className="mx-auto max-w-[480px] pt-2">
      <h1 className="heading text-[26px] tracking-[-0.02em]">Babia</h1>
      <div className="head-rule" />

      <h2 className="heading mt-8 text-[28px] leading-tight">You’re all set</h2>
      <p className="mt-2 text-[17px]">
        {names.length ? `Your log for ${namesInWords(names)} is ready.` : "Your log is ready."}
      </p>

      <section className="mt-8 border border-divider border-t-[3px] border-t-accent-2 bg-surface p-4">
        <h3 className="heading flex items-center gap-2 text-[22px]">
          <Sparkles className="size-[22px] shrink-0 text-accent-2" aria-hidden="true" />
          Talk to your log
        </h3>
        <p className="mt-2 text-[16px] leading-snug">
          Connect the AI assistant you already use, like ChatGPT, Gemini, or Claude. Then just tell
          it what happened:
        </p>
        <p className="mt-2 text-[16px] italic">“{tryPhrases(names, unit)[0]}”</p>
        <p className="mt-2 text-[16px] leading-snug">
          It goes on this log, the same as a tap, and you can ask it about the log too. Setup takes
          a couple of minutes.
        </p>
        <Link
          to="/assistant"
          onClick={() => answer(true)}
          className="primary-btn heading mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 px-4 text-[17px]"
        >
          Connect your assistant
        </Link>
      </section>

      <Link
        to="/"
        replace
        onClick={() => answer(false)}
        className="surface-btn heading mt-4 flex min-h-[52px] w-full items-center justify-center px-4 text-[17px]"
      >
        Start logging
      </Link>
      <p className="mt-3 text-center text-[14px] opacity-65">
        You can connect one any time from the{" "}
        <Sparkles className="inline size-4 align-[-3px] text-accent-2" aria-hidden="true" />
        <span className="sr-only">sparkle</span> at the top of the log.
      </p>
    </div>
  );
}
